import React, { useState, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DinePriceList, DineItem, DineItemVariant, ItemPriceInput } from 'api/types';

/**
 * The prices of one version: the whole card down the left, the card price
 * beside each dish (and each size), and a box for this list's price. Blank
 * means "not in this list — the card price applies". Saved as a whole set.
 * Read-only once the version is in force; the API refuses writes then too.
 */
interface Props {
  isOpen: boolean;
  list: DinePriceList;
  onSaved: () => void;
  onClose: () => void;
}

interface Row { itemId: string; variantId?: string; label: string; sub: string; defaultPrice: number; isOpenPrice: boolean; }
const key = (itemId: string, variantId?: string) => `${itemId}|${variantId ?? ''}`;

const DineOpsPriceListPricesModal: React.FC<Props> = ({ isOpen, list, onSaved, onClose }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [q, setQ] = useState('');
  const [onlyPriced, setOnlyPriced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const readOnly = list.state !== 'UPCOMING';

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true); setError(null); setQ(''); setOnlyPriced(false);
    (async () => {
      try {
        const [card, full] = await Promise.all([
          dineOpsService.getItems({ page: 1, pageSize: 500 }),
          dineOpsService.getPriceList(list.id),
        ]);
        const items = (card.data ?? []).filter((i: DineItem) => i.isActive);
        // Sizes: one read per dish that has any. The card says how many.
        const withVariants = items.filter((i) => i.variantCount > 0);
        const variants = await Promise.all(withVariants.map((i) => dineOpsService.getVariants(i.id).then((v) => [i.id, v] as [string, DineItemVariant[]])));
        const vmap = new Map(variants);
        const built: Row[] = [];
        for (const i of items) {
          built.push({ itemId: i.id, label: i.itemName, sub: `${i.itemCode} · ${i.courseName} › ${i.groupName}`, defaultPrice: i.price, isOpenPrice: i.isOpenPrice });
          for (const v of vmap.get(i.id) ?? []) built.push({ itemId: i.id, variantId: v.id, label: `${i.itemName} — ${v.variantName}`, sub: `size of ${i.itemCode}`, defaultPrice: v.price, isOpenPrice: i.isOpenPrice });
        }
        setRows(built);
        setPrices(Object.fromEntries((full.prices ?? []).map((p) => [key(p.itemId, p.variantId), String(p.price)])));
      } catch {
        notificationService.error('Could not load', 'The card or the price list could not be loaded.');
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, list.id]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => (!s || r.label.toLowerCase().includes(s) || r.sub.toLowerCase().includes(s))
      && (!onlyPriced || (prices[key(r.itemId, r.variantId)] ?? '') !== ''));
  }, [rows, q, onlyPriced, prices]);

  const pricedCount = useMemo(() => Object.values(prices).filter((v) => v.trim() !== '').length, [prices]);

  const save = useCallback(async () => {
    const body: ItemPriceInput[] = [];
    for (const r of rows) {
      const v = (prices[key(r.itemId, r.variantId)] ?? '').trim();
      if (v === '') continue;
      const n = Number(v);
      if (Number.isNaN(n) || n < 0) { setError(`'${r.label}': a price is a number, 0 or more.`); return; }
      body.push({ itemId: r.itemId, variantId: r.variantId, price: n });
    }
    setSaving(true); setError(null);
    try {
      await dineOpsService.savePriceListPrices(list.id, body);
      notificationService.success('Saved', `${body.length} price(s) on '${list.code}'.`);
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The prices could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [rows, prices, list, onSaved]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">{list.code} — prices{readOnly ? ' (read-only)' : ''}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {error && <div className="form-error-message" style={{ marginBottom: 12 }}>{error}</div>}
          <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
            {readOnly
              ? <><IconSystem.lock size={12} /> This version is {list.state === 'CURRENT' ? 'in force' : 'finished'}; its prices are on bills already raised. To change a price, add a <strong>new version from a date</strong> and price that.</>
              : <>Blank = not in this list; the dish sells at its card price. A size row prices that size only. Open-priced dishes take the price typed at order time and are not listed.</>}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input type="text" className="form-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the card…" style={{ flex: 1 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={onlyPriced} onChange={(e) => setOnlyPriced(e.target.checked)} /> priced only ({pricedCount})
            </label>
          </div>

          {loading && <div style={{ opacity: 0.7 }}>Loading the card…</div>}
          {!loading && (
            <table className="syut-grid" style={{ width: '100%' }}>
              <thead>
                <tr><th style={{ textAlign: 'left' }}>Dish</th><th style={{ textAlign: 'right', width: 110 }}>Card price</th><th style={{ textAlign: 'right', width: 140 }}>{list.code} price</th></tr>
              </thead>
              <tbody>
                {visible.filter((r) => !r.isOpenPrice).map((r) => {
                  const k = key(r.itemId, r.variantId);
                  const val = prices[k] ?? '';
                  return (
                    <tr key={k}>
                      <td style={{ paddingLeft: r.variantId ? 24 : 8 }}>
                        <div>{r.label}</div>
                        <div style={{ fontSize: 11, opacity: 0.6 }}>{r.sub}</div>
                      </td>
                      <td style={{ textAlign: 'right', opacity: 0.75 }}>{r.defaultPrice.toFixed(2)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <input type="number" className="form-input" style={{ width: 120, textAlign: 'right', display: 'inline-block' }}
                          value={val} min={0} step="0.01" placeholder="—" disabled={readOnly}
                          onChange={(e) => setPrices((p) => ({ ...p, [k]: e.target.value }))} />
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && <tr><td colSpan={3} style={{ padding: 10, opacity: 0.7 }}>No dish matches.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly && <button className="btn btn-primary" onClick={() => void save()} disabled={saving || loading}>{saving ? 'Saving…' : `Save ${pricedCount} price(s)`}</button>}
        </div>
      </div>
    </>
  );
};

export default DineOpsPriceListPricesModal;
